import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Button from "../../../../../../components/base/button/button";
import SaveOrRollback from "../../../../../../components/app-specific/save-or-rollback";
import classnames from "classnames";
import {Add, AddOutlined, DeleteOutlined, UnfoldLessRounded, UnfoldMoreRounded, WarningOutlined} from '@mui/icons-material';
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes} from "../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import axios from "axios";
import {useParams} from "react-router-dom";
import {Autocomplete, Box, CircularProgress, createFilterOptions, Skeleton, TextField, Tooltip, Typography} from "@mui/material";
import IconButton from "../../../../../../components/base/button/icon_button";
import {useDispatch} from "react-redux";
import DownloadAffectedProductsDialog from "../../../../../../components/dialogs/download-affected-products";
import {createUUId, deepEqual, numComparator} from "../../../../../../../core/services/utils/utils";
import {MarkProductForCacheSectionNames, ProductPropertyGroupRecordLeafTypes} from "../../../../../../../core/constants/enums";
import {Popover} from "../../../../../../components/app-specific/popover";
import Api from "../../../../../../../core/services/api/api";
import {useCachedPropertyGroupsController} from "../../../../../../hooks/use-industry";
import {setReduxDialogState} from "../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../core/models/static/routes/redux-store-routes";


const initialAffectedProductsDialogState = {open: false, productsList: [], message: '', fileTitle: ''};
const dataGridColumnNames = {
    actions: 'actions',
}


const ProductInformationPropertyGroupView = ({loading: loadingProps, groupId, setIsCacheNeeded}) => {
    const params = useParams();
    const [expandingAll, setExpandingAll] = useState(false);
    const [collapsingAll, setCollapsingAll] = useState(false);
    const [loadingSchema, setLoadingSchema] = useState(true);
    const [loadingRecords, setLoadingRecords] = useState(true);
    const [structureSchema, setStructureSchema] = useState([]);
    const [affectedProductsDialogState, setAffectedProductsDialogState] = useState(initialAffectedProductsDialogState);
    const [removingIds, setRemovingIds] = useState([]);
    const [addingNewRecord, setAddingNewRecord] = useState(false);
    const [records, setRecords] = useState([]);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const {getPropertyGroupAttributes} = useCachedPropertyGroupsController();

    const alreadyAddingBaseSchema = useMemo(() => !!records.find(e => !!e._newRecordPropertyNames && e._newRecordPropertyNames.length === structureSchema.length), [records, structureSchema.length]);
    const productId = useMemo(() => Number(params.id), [params.id]);
    const loading = useMemo(() => loadingProps || loadingSchema || loadingRecords, [loadingProps, loadingSchema, loadingRecords])
    /**
     * Fetches the structure of the property group.
     * @type {(function(*): Promise<void>)}
     */
    const getPropertyGroupStructure = useCallback(async (cancelTokenSource) => {
        setLoadingSchema(true);
        const attributes = await getPropertyGroupAttributes(groupId, cancelTokenSource.token);
        if (!isMounted())
            return;
        if (!cancelTokenSource?.token?.reason) {
            setLoadingSchema(false);
        }
        if (!!attributes) {
            const parsedStructureSchema = [...(attributes ?? [])];
            parsedStructureSchema.sort((a, b) => numComparator(a.orderIndex, b.orderIndex));
            setStructureSchema(parsedStructureSchema.map((e, i, arr) => ({
                groupPropertyId: e.id,
                title: e.title,
                propertyName: e.name,
                nextPropertyName: arr[i + 1]?.name ?? null,
                type: e.industryGroupPropertyAttrTypeId,
                isRequired: e.isRequired,
                nullReplacement: e.nullReplacement,
            })));
        }
    }, [isMounted, groupId, getPropertyGroupAttributes])

    /**
     * As soon as the component mounts:
     * - fetches the structure of the property group.
     */
    useEffect(() => {
        const axiosCancelTokenSource = axios.CancelToken.source();
        void getPropertyGroupStructure(axiosCancelTokenSource);
        return () => axiosCancelTokenSource.cancel();
    }, [getPropertyGroupStructure]);


    /**
     * Fetches the records of the property group.
     * @type {(function(*): Promise<void>)}
     */
    const getPropertyGroupRecords = useCallback(async (cancelToken) => {
        setLoadingRecords(true);
        const response = await Api.listPartIndustryGroupProperties(groupId, productId);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoadingRecords(false);
        }
        if (response?.resultFlag) {
            setRecords(response?.data?.map(e => ({
                ...e,
                data: e.content && e.content?.trim()?.length ? JSON.parse(e.content) : {},
                _newRecordPropertyNames: null, // an array of names that would suggest which properties to make editable
                _newRecordValues: [],// keys of properties to be edited, and their new values
            })) ?? []);
        }
    }, [isMounted, groupId, productId])

    /**
     * Fetches the records of the property group.
     */
    useEffect(() => {
        const axiosCancelTokenSource = axios.CancelToken.source();
        void getPropertyGroupRecords(axiosCancelTokenSource.token);
        return () => axiosCancelTokenSource.cancel();
    }, [getPropertyGroupRecords])

    /**
     * Groups the records based on the schema.
     */
    const groupRecords = useCallback((records, schema, currentLevel = 0, parentCommonId = '') => {
        if (currentLevel >= schema.length)
            return records; // Base case: return records if schema is exhausted

        const {propertyName, nextPropertyName} = schema[currentLevel];

        let newRecords = [];
        // Create a map to hold the grouped records
        const groupsMap = records.reduce((acc, record) => {
            const groupValue = record.data[propertyName]; // Group by the title of the current property
            if (!groupValue && record._newRecordPropertyNames) {
                newRecords.push(record);
                return acc;
            }

            if (!acc[groupValue]) {
                const commonId = parentCommonId ? `${parentCommonId}-${groupValue}` : ((groupValue?.toString()) ?? schema[currentLevel]?.nullReplacement ?? createUUId());
                acc[groupValue] = {
                    commonId: commonId,
                    schema: schema[currentLevel],
                    value: groupValue,
                    approved: record.approved,
                    children: [],
                    counts: {}, // Dynamic counts object to hold counts for inner properties only
                    titles: {},
                };
            }

            // acc[groupValue].approved = acc[groupValue].approved && record.approved;
            acc[groupValue].children.push(record);

            return acc;
        }, {});

        // Now, for each group, recursively group by the next property
        const groups = Object.values(groupsMap).map(group => {

            if (!schema[currentLevel + 1]?.nextPropertyName) {
                // parent of leaves
                return {
                    commonId: group.commonId, // Pass the commonId along
                    schema: group.schema,
                    value: group.value,
                    approved: group.approved,
                    records: group.children, // list of records
                    counts: {
                        [`${nextPropertyName}Count`]: group.children.length,
                    }, // Store the count for the leaf level
                    titles: {
                        [`${nextPropertyName}Title`]: [...new Set(group.children.reduce((acc, e) => {
                            if (e.content) {
                                acc.push(e.data[nextPropertyName]);
                            }
                            return acc;
                        }, []))],
                    },
                };
            }

            const groupedChildren = groupRecords(group.children, schema, currentLevel + 1, group.commonId) // Recursive call for the next level

            // Collect counts dynamically based on schema depth (excluding the current level)
            const counts = groupedChildren.reduce((totalCounts, child) => {
                // Sum counts from child groups if available
                if (child.counts) {
                    for (const countKey in child.counts) {
                        totalCounts[countKey] = (totalCounts[countKey] || 0) + child.counts[countKey];
                    }
                }
                if (child.records) {
                    const lastPropertyName = schema[schema.length - 1].propertyName;
                    totalCounts[`${lastPropertyName}Count`] = (totalCounts[`${lastPropertyName}Count`] || 0) + child.records.length;
                }
                return totalCounts;
            }, {});

            counts[`${nextPropertyName}Count`] = groupedChildren.length;

            // Collect counts dynamically based on schema depth (excluding the current level)
            const titles = groupedChildren.reduce((totalTitles, child) => {
                if (child.titles) {
                    for (const countKey in child.titles) {
                        totalTitles[countKey] = [...new Set([...(totalTitles[countKey] ?? []), ...child.titles[countKey]])];
                    }
                }
                return totalTitles;
            }, {});

            titles[`${nextPropertyName}Title`] = [...new Set(group.children.map(e => e.data[nextPropertyName]))];

            return {
                commonId: group.commonId,
                schema: group.schema,
                value: group.value,
                approved: group.approved,
                counts: counts, // Store the calculated counts for inner levels
                titles: titles,
                children: groupedChildren, // Grouped children or records
            };
        });

        if (newRecords) {
            return [...groups, ...newRecords];
        }

        return groups;
    }, []);

    /**
     * Finds the path to a group with a commonId.
     * @type {(function(*, *): (*[]))}
     */
    const findTraversedGroups = useCallback((commonId, groupedRecords) => {
        function searchGroup(group, currentPath) {
            // Add the current group to the path
            const newPath = [...currentPath, group];

            // Check if this group matches the commonId
            if (group.commonId === commonId) {
                return newPath; // If found, return the path
            }

            // If there are children, recursively search them
            if (group.children && group.children.length > 0) {
                for (const child of group.children) {
                    const result = searchGroup(child, newPath);
                    if (result) {
                        return result; // If the path is found in children, return it
                    }
                }
            }

            // If not found, return null
            return null;
        }

        // Start the search in the root level
        for (const group of groupedRecords) {
            const result = searchGroup(group, []);
            if (result) {
                return result; // Return the path once the group is found
            }
        }

        // If the group with the commonId is not found, return an empty path
        return [];
    }, [])

    /**
     * Extracts all leaves from a group.
     * @type {function(*): *[]}
     */
    const extractAllLeaves = useCallback((group) => {
        const leaves = [];

        // Helper function to recursively find all leaves
        const findLeaves = (group) => {
            // If the group has no children, it's a leaf, so add the record
            if (group.records) {
                leaves.push(...group.records);
            } else if (group.children && group.children.length > 0) {
                // Recursively check each child
                for (const child of group.children) {
                    findLeaves(child);
                }
            }
        }

        // Start the recursive process
        findLeaves(group);

        return leaves; // Return the collected leaves (records)
    }, [])

    const groupedRecords = useMemo(() => groupRecords(records, structureSchema), [groupRecords, records, structureSchema]);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...structureSchema.map((e) => ({
                name: e.propertyName,
                title: e.title,
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.left,
                pinned: false,
                sortable: false,
                resizable: false,
                pinnedToggleable: false,
                reorderable: false,
                visibilityToggleable: false,
                savable: false,
                width: 180,
            })),
            ...structureSchema.map((e) => ({
                name: `__${e.propertyName}`,
                visible: false,
                pinned: false,
                sortable: false,
                resizable: false,
                pinnedToggleable: false,
                reorderable: false,
                visibilityToggleable: false,
                savable: false,
                width: 0,
            })),
            {
                name: dataGridColumnNames.actions,
                title: '',
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.center,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
                sortable: false,
                resizable: false,
                pinnedToggleable: false,
                visibilityToggleable: false,
                width: 40,
            },
        ], [structureSchema])

    /**
     * Adds a new record to the property group to be edited/
     * @type {(function(*, null=): void)}
     */
    const addNewRecord = useCallback((propertyName, parentCommonId = null) => {
        if (!propertyName) {
            return;
        }
        const newPropertyNames = structureSchema.slice(structureSchema.findIndex(e => e.propertyName === propertyName));
        const parentPropertyNames = structureSchema.slice(0, structureSchema.findIndex(e => e.propertyName === propertyName) + 1);
        if (!parentCommonId) {
            if (propertyName === structureSchema[0].propertyName) {
                // adding a record for the first record.
                setRecords(prevState => [
                    {
                        id: createUUId(),
                        data: {},
                        _newRecordPropertyNames: newPropertyNames,
                        _newRecordValues: [],
                    },
                    ...prevState
                ])
            }
        }
        const parentGroups = findTraversedGroups(parentCommonId, groupedRecords);
        if (!parentGroups?.length)
            return;

        const data = {};
        for (let i = 0; i < parentGroups.length; i++) {
            const group = parentGroups[i];
            const parentPropertyName = parentPropertyNames[i].propertyName;
            data[parentPropertyName] = group.value;
        }
        setRecords(prevState => [...prevState, {
            id: createUUId(),
            data: data,
            _newRecordPropertyNames: newPropertyNames,
            _newRecordValues: [],
        }]);
    }, [findTraversedGroups, groupedRecords, structureSchema])


    /**
     * Saves a new record to the property group.
     * @type {(function(*, *): void)}
     */
    const saveNewRecord = useCallback(async (record, setLoading) => {
        setLoading?.(true);
        setAddingNewRecord(true);

        const entries = [];

        // Map schema properties, handling `create-value`, `free-solo`, and null cases
        const mappedValues = structureSchema.map((e, i, array) => {
            const newValue = record._newRecordValues[e.propertyName];
            return {
                attributeId: e.groupPropertyId,
                value: newValue?.id === 'null-value-here'
                    ? null
                    : newValue?.value !== undefined
                        ? newValue.value
                        : record.data[e.propertyName],

                isCreateValue: newValue?.id === 'create-value',
                isFreeSolo: newValue?.id === 'free-solo',
                isRange: e.type === ProductPropertyGroupRecordLeafTypes.range,
                last: i === array.length - 1,
            };
        });

        const noneCreateNorFreeSolo = mappedValues.every(e => !e.isCreateValue && !e.isFreeSolo && !(e.value === null && !e.last));
        const lastSchema = structureSchema[structureSchema.length - 1];
        if (noneCreateNorFreeSolo && typeof record._newRecordValues[lastSchema.propertyName]?.id === 'number') {
            const content = structureSchema.reduce((acc, e) => {
                acc[e.propertyName] = record._newRecordValues[e.propertyName]?.value !== undefined
                    ? record._newRecordValues[e.propertyName]?.value
                    : record.data[e.propertyName];
                return acc;
            }, {});
            entries.push({
                newRecord: null,
                existingRecord: {
                    partId: productId,
                    industryGroupPropertyId: groupId,
                    industryGroupPropertyRecordId: record._newRecordValues[lastSchema.propertyName].id,
                    industryGroupPropertyRecordContent: JSON.stringify(content),
                },
            });
        } else {

            /**
             * Recursively generates entries for each schema property, handling free-solo, create-value, and range values.
             * @param {number} index - The current schema property index being processed.
             * @param {Array} currentValues - The current set of attribute values collected for an entry.
             */
            function generateEntries(index, currentValues) {
                // Base case: if all schema properties are processed, push the current entry
                if (index === mappedValues.length) {
                    entries.push({
                        newRecord: {
                            industryGroupPropertyId: groupId,
                            values: currentValues,
                        },
                        existingRecord: null,
                    });
                    return;
                }

                const {attributeId, value, isCreateValue, isFreeSolo, isRange} = mappedValues[index];

                if (isCreateValue) {
                    generateEntries(index + 1, [
                        ...currentValues,
                        {attributeId, value: value},
                    ]);
                } else if (isFreeSolo && typeof value === 'string') {
                    // If `isFreeSolo`, handle as multi-record by splitting comma-separated values
                    const expandedValues = [];

                    value.split(',').forEach(part => {
                        const trimmed = part.trim();

                        if (isRange && trimmed.includes('-')) {
                            // Handle dash-separated ranges if the property is also a range type
                            const [start, end] = trimmed.split('-').map(Number);
                            if (!isNaN(start) && !isNaN(end)) {
                                for (let i = start; i <= end; i++) expandedValues.push(i);
                            }
                        } else if (!isNaN(Number(trimmed))) {
                            // Handle single number
                            expandedValues.push(Number(trimmed));
                        } else {
                            // Treat as string for non-range free-solo
                            expandedValues.push(trimmed);
                        }
                    });

                    // Recursively add each expanded value as a separate entry
                    expandedValues.forEach(expandedValue => {
                        generateEntries(index + 1, [
                            ...currentValues,
                            {attributeId, value: expandedValue},
                        ]);
                    });
                } else {
                    // For non-free-solo and non-create-value properties, add as-is and proceed
                    generateEntries(index + 1, [
                        ...currentValues,
                        {attributeId, value: value},
                    ]);
                }
            }

            // Start generating entries from the first schema property
            generateEntries(0, []);

        }

        const responseData = [];
        for (const entry of entries) {
            if (entry.newRecord) {
                const response = await Api.insertIndustryGroupProperty(entry.newRecord)
                if (!isMounted())
                    return;
                if (response?.resultFlag) {
                    entry.existingRecord = {
                        partId: productId,
                        industryGroupPropertyId: groupId,
                        industryGroupPropertyRecordId: response.data.id,
                        industryGroupPropertyRecordContent: response.data.content,
                    }
                }
            }
            if (entry.existingRecord) {
                const response = await Api.addPartIndustryGroupProperty(entry.existingRecord);
                if (!isMounted())
                    return;
                if (response?.resultFlag) {
                    responseData.push({
                        id: response.data.id,
                        industryGroupPropertyRecordId: entry.existingRecord.industryGroupPropertyRecordId,
                        content: entry.existingRecord.industryGroupPropertyRecordContent,
                    });
                }
            }
        }
        setLoading?.(false);
        setAddingNewRecord(false);
        if (responseData.length) {
            Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.propertyGroup]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
        }
        setRecords(prevState => {
            return [
                ...prevState.filter(e => e.id !== record.id),
                ...responseData.map(e => ({
                    id: e.id,
                    industryGroupPropertyRecordId: e.industryGroupPropertyRecordId,
                    content: e.content,
                    data: JSON.parse(e.content),
                    approved: false,
                    _newRecordPropertyNames: null,
                    _newRecordValues: [],
                }))
            ]
        });
    }, [groupId, isMounted, productId, structureSchema, setIsCacheNeeded])

    /**
     * Removes a group of records from the property group.
     * @type {(function(*, *, *=): void)}
     */
    const removeRecords = useCallback(async (records, title, commonId = undefined) => {
        // // TODO: call api to fetch affected parts of the following records.
        // const affectedPartsNumResponse = {
        //     resultFlag: true,
        //     data: 100
        // }
        // if (!isMounted())
        //     return;
        // const numPartsAffected = affectedPartsNumResponse?.data ?? 0;
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `${title}`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return;
                let anySuccessful = false;
                for (const record of records) {
                    setRemovingIds(prevState => [...prevState, record.id, ...(commonId ? [commonId] : [])]);
                    const response = await Api.deletePartIndustryGroupProperty(record.id);
                    if (!isMounted())
                        return;
                    setRemovingIds(prevState => prevState.filter(e => e !== record.id && (!commonId || e !== commonId)));
                    if (response?.resultFlag) {
                        anySuccessful = true;
                        setRecords(prevState => prevState.filter(e => e.id !== record.id));
                    }
                }
                if (anySuccessful) {
                    Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.propertyGroup]})
                        .then(response => {
                            if (response?.resultFlag) {
                                setIsCacheNeeded?.(true)
                            }
                        });
                }
                return anySuccessful;
            }
        }));
    }, [dispatch, isMounted, productId, setIsCacheNeeded])

    /**
     * Removes a record from the property group.
     * @type {(function(*, *): void)}
     */
    const findRecordsToRemove = useCallback((commonIdOrRecordId, isLeaf) => {
        let _records = [];
        let title = '';
        if (!isLeaf) {
            const groups = findTraversedGroups(commonIdOrRecordId, groupedRecords);
            const group = groups?.[groups.length - 1];
            if (!group)
                return;
            title = group.value;
            _records = extractAllLeaves(group);
        } else {
            const record = records.find(e => e.id === commonIdOrRecordId);
            if (!record)
                return;
            _records = [record];
            const leafSchema = structureSchema[structureSchema.length - 1];
            title = (record.data[leafSchema.propertyName] ?? (!leafSchema.isRequired ? leafSchema.nullReplacement ?? "" : ""));
        }
        if (!_records.length)
            return;
        removeRecords(_records, title, !isLeaf ? commonIdOrRecordId : undefined);
    }, [extractAllLeaves, findTraversedGroups, groupedRecords, records, removeRecords, structureSchema])

    /**
     * Groups the range records.
     * @type {(function(*, *): ([]|*[]))}
     */
    const groupRangeRecords = useCallback((_records, schema) => {
        if (!_records.length)
            return [];

        const records = _records.map(e => ({
            ...e,
            data: {
                ...e.data,
                [schema.propertyName]: (e.data[schema.propertyName] === null && !schema.isRequired) ? schema.nullReplacement : Number(e.data[schema.propertyName]),
            }
        }));
        records.sort((a, b) => {
            if (typeof a === 'string')
                return -1;
            if (typeof b === 'string')
                return 1;
            return numComparator(a.data[schema.propertyName], b.data[schema.propertyName])
        });

        const nullValues = records.filter(e => typeof e.data[schema.propertyName] !== 'number');
        const sequences = [];
        if (nullValues.length) {
            sequences.push(nullValues);
        }
        const nonNullValues = records.filter(e => typeof e.data[schema.propertyName] === 'number');
        let currentSequence
        if (nonNullValues.length) {
            currentSequence = [nonNullValues[0]];

            for (let i = 1; i < nonNullValues.length; i++) {
                // Check if the current number is consecutive to the last one in the sequence
                if (nonNullValues[i].data[schema.propertyName] === nonNullValues[i - 1].data[schema.propertyName] + 1) {
                    currentSequence.push(nonNullValues[i]);
                } else {
                    // If not consecutive, add the current sequence to the result and start a new one
                    sequences.push(currentSequence);
                    currentSequence = [nonNullValues[i]];
                }
            }
        }
        // Add the last sequence to the result
        if (currentSequence) {
            sequences.push(currentSequence);
        }
        return sequences;
    }, [])

    const dataGridRows = useMemo(() => {
        /**@type {DataGridRow[]}*/
        const flatArray = [];

        // Recursive function to traverse and flatten the grouped records
        function processGroup(group, parents = [], forceLeaf) {
            const isLeaf = !!forceLeaf || !group.commonId; // Check if this is a leaf node (a record)
            const newRecord = group._newRecordPropertyNames?.length > 0;
            const newRecordValues = Object.values(group?._newRecordValues ?? {}).filter(e => !!e);

            const defaultRecord = typeof group?.value?.content === 'string';

            const removing = isLeaf
                ? defaultRecord ? removingIds.includes(group.value.id) : removingIds.includes(group.id)
                : Array.isArray(group.value)
                    ? group.value.some(e => removingIds.includes(e.id))
                    : removingIds.includes(group.commonId)
            ;

            // Prepare the cells object for the current group or record
            const cells = {
                [dataGridColumnNames.actions]:
                    <div className={'d-flex align-items-center justify-content-end'}>
                        {
                            isLeaf && newRecord &&
                            <SaveOrRollback
                                column={false}
                                showText={false}
                                show={{
                                    save: newRecordValues.length === group._newRecordPropertyNames.length,
                                    cancel: true,
                                }}
                                onSave={(setLoading) => saveNewRecord(group, setLoading)}
                                onRollback={() => setRecords(prevState => prevState.filter(e => e.id !== group.id))}
                            />
                        }
                        {
                            !(isLeaf && newRecord) &&
                            <>
                                <IconButton
                                    color={'paper.error'}
                                    size={'small'}
                                    disabled={removing}
                                    onPressed={() => findRecordsToRemove(defaultRecord ? group.value.id : group.commonId ?? group.id, isLeaf)}
                                >
                                    {
                                        removing
                                            ? <CircularProgress size={20}/>
                                            : <DeleteOutlined className={'icon small'}/>
                                    }
                                </IconButton>
                            </>
                        }
                    </div>,
            };


            // Populate cells based on whether it's a group or record
            if (isLeaf) {
                const record = group;
                if (newRecord) {
                    for (let i = 0; i < structureSchema.length; i++) {
                        const schema = structureSchema[i];
                        const prevSchema = structureSchema[i - 1];
                        const previousPropNames = structureSchema.slice(0, i).map(e => e.propertyName);
                        const previousPropertyName = previousPropNames[previousPropNames.length - 1];
                        if (record.data[schema.propertyName]) {
                            cells[`__${schema.propertyName}`] = {groupValue: record.data[schema.propertyName]};
                        }

                        const propNames = record._newRecordPropertyNames.map(e => e.propertyName);
                        if (
                            propNames.includes(schema.propertyName) &&
                            (
                                !previousPropertyName
                                || !propNames.includes(previousPropertyName)
                                || (
                                    !!record._newRecordValues[previousPropertyName]
                                    || (!schema.isRequired && record._newRecordValues[previousPropertyName] === null)
                                )
                            )) {

                            /**
                             * Sets the value of the property.
                             */
                            const setValue = (value) => {
                                const childrenSchema = structureSchema.slice(i + 1);
                                setRecords(prevState => prevState.map(e => e.id === group.id ? {
                                    ...e,
                                    _newRecordValues: {
                                        ...e._newRecordValues,
                                        [schema.propertyName]: value,
                                        ...childrenSchema.reduce((acc, e) => ({
                                            ...acc,
                                            [e.propertyName]: undefined
                                        }), {}),
                                    }
                                } : e))
                            };

                            const filtersObj = {};
                            let prevValuesCreateValue = false;
                            for (const prevName of previousPropNames) {
                                const val = record._newRecordValues[prevName]?.value ?? record.data[prevName];
                                prevValuesCreateValue = prevValuesCreateValue || record._newRecordValues[prevName]?.id === 'create-value';
                                if (record._newRecordValues[prevName]?.id !== 'free-solo') {
                                    filtersObj[prevName] = [val];
                                }
                            }
                            cells[schema.propertyName] =
                                <ProductInformationPropertyGroupViewPropertyInput
                                    anyInput={i === structureSchema.length - 1 || schema.type === ProductPropertyGroupRecordLeafTypes.range}
                                    groupId={groupId}
                                    schema={schema}
                                    filters={filtersObj}
                                    required={schema.isRequired}
                                    nullReplacement={schema.nullReplacement}
                                    canFetchOptions={
                                        (((!record._newRecordValues[previousPropertyName] || record._newRecordValues[previousPropertyName]?.value == null) && !prevSchema?.isRequired)
                                            || (record._newRecordValues[previousPropertyName] && !!record._newRecordValues[previousPropertyName]?.value)
                                            || !!record.data[previousPropertyName]
                                            || i === 0) && !prevValuesCreateValue
                                    }
                                    value={record._newRecordValues[schema.propertyName]}
                                    setValue={setValue}
                                    disabled={removing || addingNewRecord}
                                />;

                        } else {
                            cells[schema.propertyName] = <p {...dataGridCellTextProps}/>;
                        }
                    }
                } else if (forceLeaf && !!group.schema) {
                    switch (group.schema.type) {
                        case ProductPropertyGroupRecordLeafTypes.range: {
                            const rangeRecords = group.value;
                            const removeTitle =
                                rangeRecords.length === 0
                                    ? ''
                                    : rangeRecords.length === 1
                                        ? rangeRecords[0].data[group.schema.propertyName] === null ? (group.schema.nullReplacement ?? '') : rangeRecords[0].data[group.schema.propertyName] ?? ""
                                        : `${rangeRecords[1].data[group.schema.propertyName]} to ${rangeRecords[rangeRecords.length - 1].data[group.schema.propertyName]}`;
                            cells[dataGridColumnNames.actions] =
                                <div className={'d-flex align-items-center justify-content-end'}>
                                    <IconButton
                                        color={'paper.error'}
                                        size={'small'}
                                        disabled={removing}
                                        onPressed={() => removeRecords(group.value, removeTitle, group.parentCommonId)}
                                    >
                                        {
                                            removing
                                                ? <CircularProgress size={20}/>
                                                : <DeleteOutlined className={'icon small'}/>
                                        }
                                    </IconButton>
                                </div>
                            for (const parent of parents) {
                                if (parent?.propertyName) {
                                    cells[`__${parent.propertyName}`] = {groupValue: parent.value};
                                    cells[parent.propertyName] = <p {...dataGridCellTextProps}/>;
                                }
                            }
                            if (rangeRecords.length === 0) {
                                cells[group.schema.propertyName] = '';
                            } else if (rangeRecords.length === 1) {
                                cells[group.schema.propertyName] =
                                    <div className={'d-flex align-items-center'}>
                                        {
                                            !rangeRecords[0].approved &&
                                            <Tooltip
                                                title={'This record is not yet approved, and as such, will show in the lookup system.'}>
                                                <WarningOutlined sx={{marginInlineEnd: 1}} color={'warning'}/>
                                            </Tooltip>
                                        }
                                        <p {...dataGridCellTextProps}>
                                            {rangeRecords[0].data[group.schema.propertyName] === null ? (group.schema.nullReplacement ?? "") : rangeRecords[0].data[group.schema.propertyName] ?? ""}
                                        </p>
                                    </div>;
                            } else {
                                const pluralName = group.schema.title.endsWith('y') ? `${group.schema.title.slice(1, -1)}ies` : group.schema.title.endsWith('s') ? `${group.schema.title}es` : `${group.schema.title}s`;
                                cells[group.schema.propertyName] =
                                    <Popover
                                        elevation={2}
                                        transformOrigin={{
                                            vertical: 'center',
                                            horizontal: 'center',
                                        }}
                                        child={(onEnter) =>
                                            <>
                                                <Button
                                                    onClick={onEnter}
                                                    variant={'text'}
                                                    color={'inherit'}
                                                    className={'d-flex align-items-center font-weight-normal'}
                                                    style={{gap: 4}}
                                                >
                                                    {rangeRecords[0].data[group.schema.propertyName]}
                                                    <Typography
                                                        variant={'h5'}
                                                        className={'px-2 font-weight-bold'}
                                                        sx={{color: 'primary.main', height: 14, lineHeight: 0}}
                                                    >
                                                        ...
                                                    </Typography>
                                                    {rangeRecords[rangeRecords.length - 1].data[group.schema.propertyName]}
                                                    {
                                                        !group.approved &&
                                                        <Tooltip
                                                            title={'This record is not yet approved, and as such, will show in the lookup system.'}>
                                                            <WarningOutlined sx={{marginInlineEnd: 1}} color={'warning'}/>
                                                        </Tooltip>
                                                    }
                                                </Button>
                                            </>
                                        }
                                        popoverChild={() => (
                                            <Box sx={{
                                                width: 'min(90vw, 275px)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}>
                                                <Typography
                                                    variant={'body2'}
                                                    color={'text.secondary'}
                                                    sx={{padding: 2, paddingBlockEnd: 0}}
                                                >
                                                    {pluralName} of {parents.map(e => e.value).join(' - ')}
                                                </Typography>
                                                <Box sx={{
                                                    maxHeight: '50vh',
                                                    overflowY: 'auto',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 2,
                                                    padding: 2,
                                                }}>
                                                    {
                                                        rangeRecords.map((record) => (
                                                            <div
                                                                key={record.id}
                                                                className={'d-flex align-items-center'}
                                                                style={{gap: 8}}
                                                            >
                                                                <p {...dataGridCellTextProps}>
                                                                    {record.data[group.schema.propertyName]}
                                                                </p>
                                                                {
                                                                    !record.approved &&
                                                                    <Tooltip
                                                                        title={'This record is not yet approved, and as such, will show in the lookup system.'}>
                                                                        <WarningOutlined sx={{marginInlineEnd: 1}} color={'warning'}/>
                                                                    </Tooltip>
                                                                }
                                                                <IconButton
                                                                    className={'ml-auto'}
                                                                    color={'paper.error'}
                                                                    size={'small'}
                                                                    disabled={removing}
                                                                    onPressed={() => removeRecords([record], `${record.data[group.schema.propertyName]}`)}
                                                                >
                                                                    {
                                                                        removing
                                                                            ? <CircularProgress size={20}/>
                                                                            : <DeleteOutlined className={'icon small'}/>
                                                                    }
                                                                </IconButton>
                                                            </div>
                                                        ))
                                                    }
                                                </Box>
                                            </Box>
                                        )}
                                    />;
                            }
                            break;
                        }
                        default: {
                            for (const parent of parents) {
                                if (parent?.propertyName) {
                                    cells[`__${parent.propertyName}`] = {groupValue: parent.value};
                                    cells[parent.propertyName] = <p {...dataGridCellTextProps}/>;
                                }
                            }
                            for (const schema of structureSchema) {
                                if (!(`__${schema.propertyName}` in cells)) {
                                    cells[`__${schema.propertyName}`] = {groupValue: group.value.data[schema.propertyName]};
                                }
                                if (schema.propertyName === group.schema.propertyName) {
                                    const shownValue = group.value.data[schema.propertyName] ?? (schema.nullReplacement ?? '');
                                    // Display the group's title in its corresponding column
                                    cells[schema.propertyName] =
                                        <div className={'d-flex align-items-center justify-content-end'}>
                                            <p {...dataGridCellTextProps} className={'mr-2'}>{shownValue}</p>
                                        </div>;
                                } else {
                                    cells[schema.propertyName] = <p {...dataGridCellTextProps}/>;
                                }
                            }
                            break;
                        }
                    }
                } else {
                    for (const schema of structureSchema) {
                        cells[`__${schema.propertyName}`] = {groupValue: record.data[schema.propertyName]};
                        cells[schema.propertyName] = <p {...dataGridCellTextProps}/>;
                    }
                    const lastSchema = structureSchema[structureSchema.length - 1];
                    if (lastSchema) {
                        cells[lastSchema.propertyName] =
                            <div className={'d-flex align-items-center'}>
                                {
                                    !group.approved &&
                                    <Tooltip
                                        title={'This record is not yet approved, and as such, will show in the lookup system.'}>
                                        <WarningOutlined sx={{marginInlineEnd: 1}} color={'warning'}/>
                                    </Tooltip>
                                }
                                <p {...dataGridCellTextProps}>{record.data[lastSchema.propertyName]}</p>
                            </div>;
                    }
                }
            } else {
                const alreadyAddingRecord = !!group.children?.find(e => !!e._newRecordPropertyNames) || !!group.records?.find(e => !!e._newRecordPropertyNames);
                for (const parent of parents) {
                    if (parent?.propertyName) {
                        cells[`__${parent.propertyName}`] = {groupValue: parent.value};
                    }
                }
                // If it's a group, display the group's title in its column, and show counts for inner groups
                for (const schema of structureSchema) {
                    if (!(`__${schema.propertyName}` in cells)) {
                        cells[`__${schema.propertyName}`] = {groupValue: group.value};
                    }
                    if (schema.propertyName === group.schema.propertyName) {
                        const shownValue = group.value ?? (schema.nullReplacement ?? '');
                        // Display the group's title in its corresponding column
                        cells[schema.propertyName] =
                            <>
                                <div className={'d-flex align-items-center justify-content-end'}>
                                    {
                                        !group.approved &&
                                        <Tooltip
                                            title={'This record is not yet approved, and as such, will show in the lookup system.'}>
                                            <WarningOutlined sx={{marginInlineEnd: 1}} color={'warning'}/>
                                        </Tooltip>
                                    }
                                    <p {...dataGridCellTextProps} className={'mr-2'}>
                                        {shownValue}
                                    </p>
                                    {
                                        !alreadyAddingRecord &&
                                        <Tooltip
                                            title={`Add a new ${schema.nextPropertyName ? schema.nextPropertyName : "record"} for ${shownValue}`}>
                                            <IconButton
                                                className={'icon small'}
                                                size={'small'}
                                                color={'paper.primary'}
                                                onPressed={(e) => {
                                                    const toggleButton = e.currentTarget.parentElement?.parentElement?.querySelector('.data-grid-group-toggler-button');
                                                    if (toggleButton && !toggleButton.classList.contains('open')) {
                                                        toggleButton.click();
                                                    }
                                                    addNewRecord(schema.nextPropertyName, group.commonId)
                                                }}
                                            >
                                                <AddOutlined/>
                                            </IconButton>
                                        </Tooltip>
                                    }
                                </div>
                            </>;
                    } else if (group.counts[`${schema.propertyName}Count`] !== undefined) {
                        const count = group.counts[`${schema.propertyName}Count`];
                        const titles = group.titles[`${schema.propertyName}Title`];
                        const titleGroups = schema.type === ProductPropertyGroupRecordLeafTypes.range
                            ? groupRangeRecords(titles?.map(e => ({data: {[schema.propertyName]: e}})) ?? [], schema)
                            : (titles ?? []);

                        // Display the counts for the inner groups in their respective columns
                        // end with y -> ies, end with s -> es, else add s
                        const pluralName = schema.title.endsWith('y') ? `${schema.title.slice(0, -1)}ies` : schema.title.endsWith('s') ? `${schema.title}es` : `${schema.title}s`;
                        if (titleGroups.length <= 5 && !!titles) {
                            if (schema.type === ProductPropertyGroupRecordLeafTypes.range) {
                                cells[schema.propertyName] =
                                    <p {...dataGridCellTextProps} className={'text-xs font-weight-semi-bold grey-3'}>
                                        {
                                            titleGroups.map(e => {
                                                const vals = e.map(f => f.data[schema.propertyName])
                                                if (vals.length === 1) {
                                                    return vals[0];
                                                }
                                                return `${vals[0]}...${vals[vals.length - 1]}`;
                                            }).join(', ')
                                        }
                                    </p>;
                            } else {
                                cells[schema.propertyName] =
                                    <p {...dataGridCellTextProps} className={'text-xs font-weight-semi-bold grey-3'}>
                                        {titles?.map(e => (e === null && !schema.isRequired) ? schema.nullReplacement : e ?? "").join(', ') ?? ""}
                                    </p>;
                            }
                        } else {
                            cells[schema.propertyName] =
                                <p {...dataGridCellTextProps} className={'text-xs font-weight-semi-bold grey-3'}>
                                    {`${count} ${count === 1 ? schema.title : pluralName}`}
                                </p>;
                        }
                    } else {
                        // For parent groups, leave the column empty
                        cells[schema.propertyName] = <p {...dataGridCellTextProps}/>;
                    }
                }
            }

            // Create a flat object for the current group or record
            flatArray.push({
                key: isLeaf
                    ? newRecord
                        ? `new-record-${group._newRecordPropertyNames.reduce((acc, e) => `${acc}-${e.propertyName}`, '')}`
                        : Array.isArray(group.value)
                            ? `leaves-${group.value.map(e => e.id).join('-')}`
                            : `leaf-${defaultRecord ? group.value.content : group.value}`
                    : group.commonId?.concat(forceLeaf ? '-leaves' : '') ?? createUUId(),
                data: group,
                cells: cells,
            });

            // If the group has children, recursively process them
            if (!isLeaf) {
                const newParents = [...parents, {value: group.value, propertyName: group.schema.propertyName}];
                if (group.records && !!group.records.length) {
                    const recordsSchema = group.schema.nextPropertyName ? structureSchema.find(e => e.propertyName === group.schema.nextPropertyName) : null;
                    switch (recordsSchema?.type) {
                        case ProductPropertyGroupRecordLeafTypes.range: {
                            const newRecords = group.records.filter(e => !!e._newRecordPropertyNames?.length);
                            const newRecordIds = newRecords.map(e => e.id);
                            const rangeRecords = groupRangeRecords(group.records.filter(e => !newRecordIds.includes(e.id)), recordsSchema);
                            rangeRecords.forEach(rangeRecords => processGroup(
                                {
                                    parentCommonId: group.commonId,
                                    schema: recordsSchema,
                                    value: rangeRecords,
                                    approved: group.approved,
                                    counts: {},
                                    titles: {},
                                },
                                newParents,
                                true,
                            ));
                            newRecords.forEach(record => processGroup(record, newParents));
                            break;
                        }
                        default: {
                            const newRecords = group.records.filter(e => !!e._newRecordPropertyNames?.length);
                            const newRecordIds = newRecords.map(e => e.id);
                            const otherRecords = group.records.filter(e => !newRecordIds.includes(e.id));
                            otherRecords.forEach(record => processGroup({
                                commonId: group.commonId,
                                schema: recordsSchema,
                                value: record,
                                approved: group.approved,
                                counts: {},
                            }, newParents, true));
                            newRecords.forEach(record => processGroup(record, newParents));
                            break;
                        }
                    }
                } else if (group.children && !!group.children.length) {
                    group.children.forEach(child => processGroup(child, newParents));
                }
            }
        }

        // Start processing the root groups
        groupedRecords.forEach(group => processGroup(group));

        return flatArray;
    }, [groupedRecords, removingIds, saveNewRecord, findRecordsToRemove, structureSchema, groupId, addingNewRecord, removeRecords, addNewRecord, groupRangeRecords])

    /**@type {DataGridRowGroup[]} */
    const dataGridGroups = useMemo(() => {
        return structureSchema.map(e => ({
            columnName: `__${e.propertyName}`,
            showOnColumn: e.propertyName,
        }))
    }, [structureSchema])

    /**
     * Expands All the rows of the data-grid.
     * @type {(function())}
     */
    const expandAll = useCallback(async () => {
        setExpandingAll(true);
        const dataGrid = document.querySelector('.property-group-data-grid');
        for (let i = 0; i < structureSchema.length; i++) {
            const toggleButtons = dataGrid?.querySelectorAll('.data-grid-group-toggler-button:not(.open):not(.invisible)');
            for (const toggleButton of toggleButtons) {
                toggleButton.click();
            }
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        setExpandingAll(false);
    }, [structureSchema])

    /**
     * Collapses All the rows of the data-grid.
     * @type {(function())}
     */
    const collapseAll = useCallback(async () => {
        setCollapsingAll(true);
        const dataGrid = document.querySelector('.property-group-data-grid');
        for (let i = 0; i < structureSchema.length; i++) {
            const toggleButtons = dataGrid?.querySelectorAll('.data-grid-group-toggler-button.open:not(.invisible)');
            for (const toggleButton of toggleButtons) {
                toggleButton.click();
            }
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        setCollapsingAll(false);
    }, [structureSchema])


    const schemasWithRangeRecords = useMemo(() => structureSchema.filter(e => e.type === ProductPropertyGroupRecordLeafTypes.range), [structureSchema]);

    return (
        <>
            <div className={'product-property-group-view part-info-paper pb-5'}>
                <div>
                    {
                        schemasWithRangeRecords.length > 0 &&
                        <Typography variant={'body2'}
                                    sx={{
                                        width: '100%',
                                        padding: 3,
                                        strong: {color: 'secondary.main'},
                                        b: {color: 'tertiary.main'},
                                        lineHeight: 2
                                    }}>
                            The following properties have range
                            records: <strong>{schemasWithRangeRecords.map(schema => schema.propertyName).join(', ')}</strong> <br/>
                            That means that you can add one or multiple records using the following formula: <br/>
                            <ul>
                                <li>
                                    <b>value</b> to add a single value <br/>
                                </li>
                                <li>
                                    <b>start - end</b> to add a range of values from <b>start</b> to <b>end</b> separated by '-' (dash
                                    separated)<br/>
                                </li>
                                <li>
                                    <b>value1, value2, value3</b> to add multiple values separated by ',' (commad separted)<br/>
                                </li>
                            </ul>
                        </Typography>
                    }

                </div>
                <div className={'d-flex justify-content-between my-3 mx-3 align-items-center'}>
                    <div className={'d-flex align-items-center'} style={{gap: 4}}>
                        {
                            !loading &&
                            <>
                                <Button
                                    variant={'outlined'}
                                    color={'secondary'}
                                    className={'d-flex align-items-center'}
                                    disabled={expandingAll || collapsingAll}
                                    onClick={() => expandAll()}
                                >
                                    <UnfoldMoreRounded className={'icon small'}/>
                                    Expand all rows
                                </Button>
                                <Button
                                    variant={'outlined'}
                                    color={'secondary'}
                                    className={'d-flex align-items-center'}
                                    disabled={expandingAll || collapsingAll}
                                    onClick={() => collapseAll()}
                                >
                                    <UnfoldLessRounded className={'icon small'}/>
                                    Collapse all rows
                                </Button>
                            </>
                        }
                    </div>
                    {
                        !alreadyAddingBaseSchema &&
                        <Button
                            className={classnames('d-flex align-items-center justify-content-center',)}
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => addNewRecord(structureSchema[0]?.propertyName)}
                            disabled={loading || loadingSchema}
                        >
                            <Add className={'mr-2'}/>
                            <p>
                                {
                                    loading || loadingSchema
                                        ? "Loading..."
                                        : `Add a new ${structureSchema[0]?.propertyName ?? 'record'}`
                                }
                            </p>
                        </Button>
                    }
                </div>
                <Box sx={{
                    '.data-grid-loading-container': {
                        marginTop: `0 !important`,
                    },
                }}>
                    {
                        structureSchema.length > 1 &&
                        <PartnerDataGrid
                            initialLayoutState={{columns: columns,}}
                            classNames={{toolbar: 'pt-0', container: 'property-group-data-grid'}}
                            rows={dataGridRows}
                            columns={columns}
                            hideToolbar
                            hideFooter
                            state={{
                                loading: {state: loading},
                                group: dataGridGroups,
                            }}
                        />
                    }
                </Box>
            </div>
            <DownloadAffectedProductsDialog
                open={affectedProductsDialogState.open}
                onClose={() => setAffectedProductsDialogState(initialAffectedProductsDialogState)}
                productList={affectedProductsDialogState.productsList}
                message={affectedProductsDialogState.message}
                fileTitle={affectedProductsDialogState.fileTitle}
            />
        </>
    )
}

const ProductInformationPropertyGroupViewPropertyInput = ({
                                                              anyInput,
                                                              groupId,
                                                              schema,
                                                              filters,
                                                              value,
                                                              setValue,
                                                              disabled,
                                                              canFetchOptions,
                                                              required,
                                                              nullReplacement,
                                                          }) => {
    const [options, setOptions] = useState([]);
    const defaultOptions = useMemo(() => required ? [] : options.some(e => e.value === null) ? [] : [{
        id: 'null-value-here',
        value: null,
    }], [required, options]);
    const [loading, setLoading] = useState(false);
    const [getOptions, setGetOptions] = useState(false);
    const isMounted = useIsMounted();
    const focusedRef = useRef(false);
    const fetchedFilters = useRef();

    /**
     * Fetches the options from the api.
     */
    const fetchOptions = useCallback(async (cancelToken) => {
        const parsedFilters = Object.fromEntries(Object.entries(filters).map(([key, values]) => {
            const sortedValues = [...values];
            sortedValues.sort((a, b) => a.localeCompare(b));
            return [
                key,
                sortedValues,
            ]
        }));
        if (fetchedFilters.current && deepEqual(fetchedFilters.current, parsedFilters))
            return;
        fetchedFilters.current = parsedFilters;
        setLoading(true);
        const forApi = {
            industryGroupPropertyId: groupId,
            searchQuery: filters,
            returnAttrId: schema.groupPropertyId,
        }
        const response = await Api.queryIndustryGroupProperties(forApi, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag) {
            response.data = response.data.map(e => ({
                id: e.externalId,
                value: e.value,
            }))
            const uniqueOptionKeys = [...new Set(response.data?.map(e => e.value) ?? []).values()];
            setOptions(uniqueOptionKeys.map((key) => response.data?.find(e => e.value === key)));
        }
    }, [isMounted, filters, groupId, schema.groupPropertyId]);

    /**
     * With each change in the [useApi] prop: (basically as soon as the component is mounted and the column has an api url)
     * - fetches the options.
     */
    useEffect(() => {
        if (!getOptions || !canFetchOptions)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void fetchOptions(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [fetchOptions, getOptions, canFetchOptions]);

    const filterOptionsAlgo = useMemo(() => createFilterOptions({
        ignoreCase: true,
        limit: 100,
        stringify: (option) => option?.value?.toString() ?? "",
    }), []);

    /**
     * Sets the selected option in the state.
     * @type {(function(*, *, *): void)}
     */
    const onChange = useCallback((event, value, reason, _, force = false) => {
        if (reason !== 'clear') {
            focusedRef.current = false;
        }
        if (typeof value === 'string' && !value.trim().length) {
            return setValue(null);
        }
        if (force) {
            setValue(typeof value === 'string' ? {value: value, id: 'free-solo'} : value);
        } else {
            setValue(typeof value === 'string' ? {value: value, id: null} : value);
        }
    }, [setValue])

    /**
     * Filters the options based on the input
     * @type {function(*, *): unknown[]}
     */
    const filterOptions = useCallback((options, params) => {
        const filtered = filterOptionsAlgo(options, params);
        if (
            !anyInput &&
            params.inputValue?.trim()?.length &&
            !filtered.find(e => e.value?.toString() === params.inputValue?.trim())
        ) {
            filtered.push({
                value: params?.inputValue ?? "",
                id: 'create-value',
            });
        }
        return filtered;
    }, [filterOptionsAlgo, anyInput])

    return (
        <>
            <Autocomplete
                fullWidth
                blurOnSelect
                autoFocus
                onFocus={() => {
                    setGetOptions(true)
                    focusedRef.current = true;
                }}
                onBlur={() => {
                    setTimeout(() => {
                        focusedRef.current = false;
                    }, 300)
                }}
                options={[...defaultOptions, ...options]}
                value={value ?? ""}
                loading={loading}
                freeSolo
                loadingText={
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                        {
                            Array.from({length: 4}).map((_, key) => (
                                <Skeleton
                                    key={key}
                                    variant="rectangular"
                                    width={'100%'}
                                    sx={(theme) => ({minHeight: 30, borderRadius: (theme).vars?.shape.borderRadius})}
                                />
                            ))
                        }
                    </Box>
                }
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                disabled={disabled}
                readOnly={disabled}
                filterOptions={filterOptions}
                onChange={onChange}
                size={'small'}
                getOptionLabel={(option) => {
                    if (option?.id === 'null-value-here') {
                        return nullReplacement;
                    }
                    if (option?.value === null) {
                        return nullReplacement;
                    }
                    return option?.value?.toString() ?? ""
                }}
                renderOption={(props, option) => {
                    if (option.id === 'null-value-here') {
                        return (
                            <Box {...props} key={props.key}>
                                {nullReplacement}
                            </Box>
                        )
                    }
                    if (option.id === 'create-value') {
                        return (
                            <Box
                                {...props}
                                key={props.key}
                                sx={theme => ({
                                    paddingInline: 2,
                                    paddingBlock: 1,
                                    marginInline: 'auto',
                                    backgroundColor: `${theme.palette.primary.main} !important`,
                                    color: `${theme.palette.primary.contrastText} !important`,
                                    borderRadius: 1,
                                })}
                            >
                                Create "{option.value}"
                            </Box>
                        );
                    }
                    return (
                        <Box {...props} key={props.key}>
                            {option.value === null ? nullReplacement : option.value ?? ''}
                        </Box>
                    );
                }}
                getOptionKey={(option) => option?.value ?? ""}
                componentsProps={{
                    clearIndicator: {color: 'default'},
                    popupIndicator: {color: 'default'},
                }}
                renderInput={(params) =>
                    <TextField
                        {...params}
                        size={'small'}
                        label={schema.title}
                        InputProps={{
                            ...params.InputProps,
                            ...(anyInput && {
                                onChange: (e) => {
                                    onChange(e, e.target.value, 'blur', null, true);
                                },
                            }),
                            endAdornment: (
                                <React.Fragment>
                                    {loading ? <CircularProgress color="inherit" size={20}/> : null}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        }}
                    />
                }
            />
        </>
    );
}

export default ProductInformationPropertyGroupView;
